import { type Publication, publicationSchema } from "./contract";
import demo from "./moteur/livres/demo-quai.json";
import type { Livre } from "./moteur/types";
import { type Rangement, rangementDuNavigateur } from "./rangement";

/**
 * Le livre que joue l'appli. Un seul a la fois : les QR codes d'un livre imprime ne disent pas
 * de quel livre ils viennent, seulement leur code (`/c/<code>`), et deux livres pourraient
 * partager un code.
 *
 * C'est l'edition telephone d'un livre compile : sa structure sans ses textes. La console du Lab
 * la publie dans l'appli (`PUT /api/livre`, chapeau #276), et la page la charge au demarrage,
 * une seule fois (`livreCharge`, que les routes attendent) :
 *
 * 1. le livre publie (`GET /api/livre`), s'il arrive en moins de `DELAI_DU_LIVRE_MS` et passe le
 *    contrat (`packages/shared/src/app/livre.ts` : aucun texte, forme stricte, coherence) ; une
 *    copie en est gardee dans le stockage local ;
 * 2. sinon la derniere copie gardee : un livre recu une fois se rejoue sans reseau ;
 * 3. sinon la demo embarquee dans le bundle, ci-dessous.
 *
 * La demo reste importee en tete de ce fichier : `e2e/livre-jeu/outils.ts` lit cette ligne pour
 * savoir quel livre jouent les e2e, dont la pile ne publie aucun livre.
 */

// Le JSON importe a des types larges (`format: number`, `fin: string`) : il est conforme a
// `Livre` parce qu'il sort du compilateur, ce que verifie appli.test.ts.
export const livreEmbarque = demo as unknown as Livre;

/** Ou le navigateur garde le dernier livre publie recu. Les parties, elles, ont leur cle par livre. */
export const CLE_DU_LIVRE = "livre-jeu:livre-publie";

/** Ce que la page attend le livre publie avant de jouer sa copie gardee, ou la demo. */
export const DELAI_DU_LIVRE_MS = 4_000;

export type Provenance = "publie" | "garde" | "embarque";

export interface LivreCharge {
  livre: Livre;
  provenance: Provenance;
}

export interface OptionsDeChargement {
  /** `fetch` par defaut, lu au moment de l'appel. */
  chercher?: typeof fetch;
  rangement?: Rangement;
  delaiMs?: number;
}

/**
 * Le livre publie, ou `null` : pas de reponse a temps, une erreur, un JSON hors contrat. Ne
 * leve jamais. `credentials: "omit"` : la requete ne porte aucun cookie, et le navigateur
 * ignore celui que la couche Lab du backend pose sur ses reponses (comme `mesure.ts`).
 */
async function livrePublie(chercher: typeof fetch, delaiMs: number): Promise<Publication | null> {
  const controleur = new AbortController();
  let minuterie: ReturnType<typeof setTimeout> | undefined;
  const delai = new Promise<null>((resoudre) => {
    minuterie = setTimeout(() => {
      controleur.abort();
      resoudre(null);
    }, delaiMs);
  });
  const lecture = (async () => {
    const reponse = await chercher("/api/livre", {
      headers: { accept: "application/json" },
      credentials: "omit",
      cache: "no-store",
      signal: controleur.signal,
    });
    if (!reponse.ok) return null;
    const lu = publicationSchema.safeParse(await reponse.json());
    return lu.success ? lu.data : null;
  })().catch(() => null);
  try {
    return await Promise.race([lecture, delai]);
  } finally {
    clearTimeout(minuterie);
  }
}

/** La copie gardee, si elle se relit et passe encore le contrat. */
function copieGardee(rangement: Rangement): Publication | null {
  try {
    const brut = rangement.lire(CLE_DU_LIVRE);
    if (brut === null) return null;
    const lu = publicationSchema.safeParse(JSON.parse(brut));
    return lu.success ? lu.data : null;
  } catch {
    // Stockage refuse (navigation privee) ou copie abimee : comme s'il n'y en avait pas.
    return null;
  }
}

function garder(rangement: Rangement, publication: Publication): void {
  try {
    rangement.ecrire(CLE_DU_LIVRE, JSON.stringify(publication));
  } catch {
    // Stockage refuse ou plein : le livre se joue, il ne sera simplement pas garde.
  }
}

/** Charge le livre de l'appli : le publie, sinon la copie gardee, sinon la demo. Ne rejette jamais. */
export async function chargerLivre(options: OptionsDeChargement = {}): Promise<LivreCharge> {
  const rangement = options.rangement ?? rangementDuNavigateur();
  const chercher = options.chercher ?? ((entree, init) => fetch(entree, init));
  const publication = await livrePublie(chercher, options.delaiMs ?? DELAI_DU_LIVRE_MS);
  if (publication) {
    garder(rangement, publication);
    return { livre: publication.livre, provenance: "publie" };
  }
  const garde = copieGardee(rangement);
  if (garde) return { livre: garde.livre, provenance: "garde" };
  return { livre: livreEmbarque, provenance: "embarque" };
}

let actif: Livre = livreEmbarque;
let chargement: Promise<Livre> | null = null;

/**
 * Le livre de la page, charge une seule fois : les routes l'attendent avant d'afficher quoi que
 * ce soit ou de lire un code (`routes.tsx`), et toutes partagent la meme promesse. Ne rejette
 * jamais : au pire, c'est la demo.
 */
export function livreCharge(): Promise<Livre> {
  chargement ??= chargerLivre().then(({ livre }) => {
    actif = livre;
    return livre;
  });
  return chargement;
}

/** Le livre charge ; la demo tant que le chargement n'a pas abouti. */
export function livreActif(): Livre {
  return actif;
}

/** Pour les tests : oublie le chargement, la page repart de la demo. */
export function oublierLeLivreCharge(): void {
  actif = livreEmbarque;
  chargement = null;
}
